"use client";

import Link from "next/link";
import { safeHref } from "@/lib/safe-href";
import * as React from "react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import type { VariantProps } from "class-variance-authority";
import type { HeroContent } from "./types";

type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>["variant"]>;

type Props = {
  content: HeroContent;
  primaryVariant?: ButtonVariant;
  secondaryVariant?: ButtonVariant;
  badgeClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  /** Refs the parent timeline targets for staggered entrance. */
  refs?: {
    badge?: React.RefObject<HTMLSpanElement | null>;
    title?: React.RefObject<HTMLHeadingElement | null>;
    subtitle?: React.RefObject<HTMLParagraphElement | null>;
    ctas?: React.RefObject<HTMLDivElement | null>;
  };
};

export function HeroBody({
  content,
  primaryVariant = "primary",
  secondaryVariant = "outline",
  badgeClassName,
  titleClassName,
  subtitleClassName,
  refs,
}: Props) {
  const { badge, title, subtitle, primaryCta, secondaryCta } = content;

  return (
    <div className="relative z-10 mx-auto flex w-full max-w-5xl flex-col items-center px-2 text-center md:px-4">
      {badge ? (
        <span
          ref={refs?.badge}
          className={cn(
            "mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/[0.07] px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.24em] text-[var(--accent)] shadow-sm backdrop-blur-md",
            badgeClassName
          )}
        >
          <span
            aria-hidden
            className="size-1.5 shrink-0 rounded-full bg-[var(--accent)] shadow-[0_0_8px_var(--accent)]"
          />
          {badge}
        </span>
      ) : null}

      <h1
        ref={refs?.title}
        className={cn(
          "hero-title-glow text-balance text-4xl font-extrabold leading-[1.04] tracking-tight text-[var(--hero-title)] md:text-6xl lg:text-[4.25rem] lg:leading-[1.02]",
          titleClassName
        )}
      >
        {title}
      </h1>

      {subtitle ? (
        <p
          ref={refs?.subtitle}
          className={cn(
            "mx-auto mt-6 max-w-2xl text-pretty text-base leading-relaxed text-[var(--hero-subtitle)] md:mt-7 md:text-lg md:leading-8",
            subtitleClassName
          )}
        >
          {subtitle}
        </p>
      ) : null}

      {(primaryCta || secondaryCta) && (
        <div
          ref={refs?.ctas}
          className="mt-10 flex flex-col items-center justify-center gap-3 sm:mt-12 sm:flex-row sm:gap-4"
        >
          {primaryCta ? (
            <Link
              href={safeHref(primaryCta.href)}
              className={cn(buttonVariants({ variant: primaryVariant, size: "lg" }))}
            >
              {primaryCta.label}
            </Link>
          ) : null}
          {secondaryCta ? (
            <Link
              href={safeHref(secondaryCta.href)}
              className={cn(buttonVariants({ variant: secondaryVariant, size: "lg" }))}
            >
              {secondaryCta.label}
            </Link>
          ) : null}
        </div>
      )}
    </div>
  );
}
